import {
  inject,
  Injectable,
} from '@angular/core';
import {JwtService} from '../../jwt/jwt.service';
import {
  ApplicationRole,
  BusinessRole,
  RoleType,
} from '../../../../../common/enums/role.enum';
import {AppTranslateService} from '../../../page/i18n/translate/app-translate/app-translate.service';
import {USER_ROLE_LABEL_KEYS} from '../../../../../common/constants/account-labels';

@Injectable({
  providedIn: 'root',
})
export class UserAclService {

  private jwtService = inject(JwtService);

  private appTranslateService = inject(AppTranslateService);

  /**
   * Method: Checks if the user has application admin roles.
   * @returns {boolean} True if the user has admin or developer roles, false otherwise.
   */
  public isApplicationAdmin(): boolean {
    return this.hasAnyOfRoles([ApplicationRole.ADMIN, ApplicationRole.DEVELOPER]);
  }

  /**
   * Method: Checks if the user has the developer role.
   * @returns {boolean} True if the user has the developer role, false otherwise.
   */
  public isDeveloperRole(): boolean {
    return this.hasApplicationRole(ApplicationRole.DEVELOPER);
  }

  /**
   * Method: Checks if the current user is a library admin.
   * @returns {boolean} True if the user is a library admin, false otherwise.
   */
  public isLibraryAdmin(): boolean {
    return this.hasApplicationRole(ApplicationRole.LIBRARY_ADMIN);
  }

  /**
   * Method: Check access for broker route
   */
  public hasBrokerAreaAccess(): boolean {
    return this.isLibraryAdmin() || this.isBrokerRole() || this.isApplicationAdmin();
  }

  /**
   * Method: Checks if the user is eligible to access the broker dashboard.
   * @returns {boolean} True if the user is eligible for the broker dashboard, false otherwise.
   */
  public isBrokerDashboardEligible(): boolean {
    return !this.isLibraryAdmin()
      && (this.isBrokerRole() || this.isApplicationAdmin());
  }

  /**
   * Method: Check guest access, role can appear in application token and usually token
   * @returns {boolean} True if the user is a guest
   */
  public isGuestRole(): boolean {
    return this.hasAnyOfRoles([ApplicationRole.GUEST]);
  }

  public isBrokerAdminOrInsurerAdmin(): boolean {
    return this.isBrokerAdmin() || this.isInsurerAdmin();
  }

  /**
   * Method: Check insurer admin access
   */
  public isInsurerAdmin(): boolean {
    return this.isInsurerOrInsurerCompanyRole();
  }

  /**
   * Method: Check insurer company access
   */
  public isInsurerOrInsurerCompanyRole(): boolean {
    return this.hasAnyOfRoles([BusinessRole.INSURANCE_COMPANY, RoleType.INSURER]);
  }

  /**
   * Method: Check role Insurer Content Creator
   */
  public isContentCreatorRole(): boolean {
    return this.hasAnyOfRoles([BusinessRole.INSURANCE_COMPANY, BusinessRole.INSURANCE_EMPLOYEE]);
  }

  /**
   * Method: Check InsurerEmployees  access
   */
  public isInsurerEmployees(): boolean {
    return this.hasAnyOfRoles([BusinessRole.INSURANCE_EMPLOYEE]);
  }

  /**
   * Method: Check insurer access
   */
  public isInsurerRole(): boolean {
    return this.hasAnyOfRoles([BusinessRole.INSURANCE_COMPANY, BusinessRole.INSURANCE_EMPLOYEE, RoleType.INSURER]);
  }

  /**
   * Method: Check insurer role type access
   */
  public isInsurerRoleType(): boolean {
    return this.jwtService.getCompanyDiscriminator() === RoleType.INSURER;
  }

  /**
   * Method: Check broker role type access
   */
  public isBrokerRoleType(): boolean {
    return this.jwtService.getCompanyDiscriminator() === RoleType.BROKER;
  }

  /**
   * Method: Check broker access
   */
  public isBrokerRole(): boolean {
    return this.hasAnyOfRoles([BusinessRole.BROKER_COMPANY, BusinessRole.BROKER_EMPLOYEE, RoleType.BROKER]);
  }

  /**
   * Method: Check broker company and broker employee access
   */
  public isBrokerCompanyOrEmployee(): boolean {
    return this.hasAnyOfRoles([BusinessRole.BROKER_COMPANY, BusinessRole.BROKER_EMPLOYEE]);
  }

  /**
   * Method: Check broker employee (broker user) access
   */
  public isBrokerEmployee(): boolean {
    return this.hasAnyOfRoles([BusinessRole.BROKER_EMPLOYEE]);
  }

  /**
   * Method: Check broker admin access
   */
  public isBrokerAdmin(): boolean {
    return this.isBrokerOrBrokerCompanyRole();
  }

  /**
   * Method: Check broker or broker company access
   */
  private isBrokerOrBrokerCompanyRole(): boolean {
    return this.hasAnyOfRoles([BusinessRole.BROKER_COMPANY, RoleType.BROKER]);
  }

  /**
   * Method: Check entrypoint role access
   */
  public isEntrypointRole(): boolean {
    return this.hasAnyOfRoles([BusinessRole.ENTRYPOINT_ADMIN, BusinessRole.ENTRYPOINT_EMPLOYEE]);
  }

  /**
   * Method: Check if the user has the Platform Member (MEMBER) role
   */
  public isPlatformMember(): boolean {
    return this.hasAnyOfRoles([BusinessRole.MEMBER]);
  }

  /**
   * Method: Check if the user is an Insurer Platform Member
   */
  public isInsurerPlatformMember(): boolean {
    return this.isPlatformMember() && this.isInsurerRoleType();
  }

  /**
   * Method: Check if the user is a Broker Platform Member
   */
  public isBrokerPlatformMember(): boolean {
    return this.isPlatformMember() && this.isBrokerRoleType();
  }

  /**
   * Method: Get role current account
   */
  public getCurrentRoleName(): string {
    const userRole = this.jwtService.getUserRoleName() as ApplicationRole | BusinessRole;
    return this.appTranslateService.getTranslation(USER_ROLE_LABEL_KEYS[userRole]) || '';
  }

  /**
   * Method: Check roles by str's
   * @param roles
   */
  private hasAnyOfRoles(roles: string[]): boolean {
    return (
      roles.filter(role => this.hasRole(role)).length > 0
      || roles.filter(role => this.hasApplicationRole(role)).length > 0
    );
  }

  /**
   * Method: Check role by str
   * @param role
   */
  private hasRole(role: string): boolean {
    return this.jwtService.getRole() === role;
  }

  /**
   * Method: Check application_role by str
   * @param role
   */
  private hasApplicationRole(role: string): boolean {
    return this.jwtService.getApplicationRole() === role;
  }

}
